<template>
  <div class="dept-mgr">

    <header class="masthead">
      <h1 class="title">Departments<span class="dot">.</span></h1>
      <p class="sub">Whoever is set here appears at the top of their department on the display.</p>
    </header>

    <p v-if="loading" class="msg">Reading departments</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="groups.length === 0" class="msg">
      No departments recorded in Entra yet. Vero reads the department field on
      each person, and it is not set on anyone in this directory.
    </p>

    <div v-else class="countries">
      <section v-for="group in groups" :key="group.code" class="country">
        <h2 class="country-name">
          <span class="chip">{{ group.code }}</span>
          {{ group.name }}
        </h2>

        <ul class="departments">
          <li v-for="dept in group.departments" :key="dept.name" class="dept">
            <div class="dept-name">
              {{ dept.name }}
              <span class="dept-count">
                {{ dept.people.length }} {{ dept.people.length === 1 ? 'person' : 'people' }}
              </span>
            </div>

            <select
              class="picker"
              :value="chosen(group.code, dept.name)"
              @change="choose(group.code, dept.name, $event.target.value)"
            >
              <option value="">No manager set</option>
              <option v-for="p in dept.people" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
          </li>
        </ul>
      </section>
    </div>

    <p v-if="saveError" class="msg err">{{ saveError }}</p>

  </div>
</template>


<script setup>
import { ref, onMounted } from 'vue'
import { getSiteHierarchy, getPeopleInCountry } from '@/services/GraphService'
import {
  getDepartmentManagers, setDepartmentManager,
  clearDepartmentManager, managerFor
} from '@/services/DepartmentManagerService'

const groups = ref([])
const managers = ref({})
const loading = ref(true)
const error = ref(null)
const saveError = ref(null)


function chosen(code, department) {
  return managerFor(managers.value, code, department) ?? ''
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const [hierarchy, managerMap] = await Promise.all([
      getSiteHierarchy(),
      getDepartmentManagers()
    ])

    managers.value = managerMap

    const out = []

    for (const country of hierarchy?.countries ?? []) {
      // Everyone in the country, in one call.
      //
      // This used to ask per office and pool the results, which meant anybody
      // with a blank officeLocation was never fetched. Most of this directory
      // has no office set, so the page showed no departments at all and looked
      // as though Entra held nothing.
      const people = (await getPeopleInCountry(country.code)) ?? []

      const byDept = new Map()
      for (const p of people) {
        const dept = p.department || 'Unassigned'
        if (!byDept.has(dept)) byDept.set(dept, [])
        byDept.get(dept).push(p)
      }

      const departments = [...byDept.entries()]
        .map(([name, list]) => ({
          name,
          people: list.sort((a, b) => a.name.localeCompare(b.name))
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

      if (departments.length > 0) {
        out.push({ code: country.code, name: country.name, departments })
      }
    }

    groups.value = out
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function choose(code, department, personId) {
  saveError.value = null

  const key = `${code}|${department}`
  const previous = managers.value[key]

  // Applied immediately then saved; waiting for the round trip makes the
  // dropdown feel broken. Reverted below if the save fails.
  const next = { ...managers.value }
  if (personId) next[key] = personId
  else delete next[key]
  managers.value = next

  try {
    if (personId) await setDepartmentManager(code, department, personId)
    else await clearDepartmentManager(code, department)
  } catch (err) {
    console.error(err)
    saveError.value = err.message ?? String(err)

    const reverted = { ...managers.value }
    if (previous === undefined) delete reverted[key]
    else reverted[key] = previous
    managers.value = reverted
  }
}

onMounted(load)
</script>


<style scoped>
.dept-mgr {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;

  /* Left aligned, like every other page. Centring made the content drift right
     of the menu and the header logo, so nothing lined up with anything else. */
  max-width: 860px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 8px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  color: var(--ink-soft);
  margin: 8px 0 0;
}

/* ---------- Countries ---------- */

.countries {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.country { padding-top: 22px; }

.country-name {
  display: flex;
  align-items: center;
  gap: 11px;
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px;
  color: var(--ink);
}

.chip {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--ink);
  color: #fff;
  padding: 4px 9px;
  border-radius: 5px;
}

/* ---------- Departments ---------- */

.departments {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dept {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 15px 0;
  border-bottom: 1px solid var(--line);
}

.dept:last-child { border-bottom: none; }

.dept-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  min-width: 0;
}

.dept-count {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: var(--ink-soft);
  margin-top: 2px;
}

.picker {
  font-family: inherit;
  font-size: 14.5px;
  height: 42px;
  min-width: 240px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #ffffff;
  color: #191c1e;
  cursor: pointer;
  outline: none;
  flex: none;
}

.picker:focus { border-color: var(--ink); }

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 10px;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 660px) {
  .dept-mgr { padding: 0 20px 60px; }
  .dept { flex-direction: column; align-items: stretch; gap: 10px; }
  .picker { min-width: 0; width: 100%; }
}
</style>
